/**
 * Contact Form Section
 * Submits to CMS feedback API
 */
import React, { useState, useEffect } from 'react';
import { Send, User, Mail, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Button from '../../../components/common/Button/Button';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const CONTACT_SECTION_UID = 'CONTACT-SECTION';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: CONTACT_SECTION_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            setSectionData({
              Title: data.Title,
              SubTitle: data.SubTitle,
              Description: data.Description,
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch contact section:', err);
      }
    };

    fetchSectionData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        Actor: 'PUBLIC',
        Email: formData.email,
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Message: formData.subject ? `[${formData.subject}] ${formData.message}` : formData.message,
        MobileNumber: formData.phone,
      };

      await axios.post(
        `${CMS_API_URL}feedback/create`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        }
      );

      setIsSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      {/* CONTACT-SECTION form */}
      <div className="text-center mb-6">
            <span className="text-yellow-600 font-semibold text-sm uppercase tracking-wide">
              {sectionData?.Title}
            </span>
            <h2 className="text-3xl font-bold text-[#2f3951] mt-2">
              {sectionData?.SubTitle}
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4"></div>
            <p className="text-gray-600 mt-4">
              {sectionData?.Description
                ? sectionData.Description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim()
                : ""}
            </p>
          </div>

          {/* Success Message */}
          {isSubmitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-800 mb-2">Message Sent!</h3>
              <p className="text-green-700 mb-4">
                Thank you for contacting us. We'll respond to your inquiry within 24 hours.
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Send another message →
              </button>
            </div>
          ) : (
            /* Contact Form */
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* First Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    First Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all"
                      placeholder="Enter your first name"
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Last Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all appearance-none bg-white"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="membership">Membership Application</option>
                    <option value="loan">Loan Information</option>
                    <option value="savings">Savings Account</option>
                    <option value="complaint">Complaint</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Message *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Enter your message here..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="default"
                isLoading={isLoading}
                className="w-full py-3 bg-[#2f3951] hover:bg-[#1e2740] text-white font-semibold rounded-lg transition-colors flex items-center justify-center"
              >
                <Send className="w-5 h-5 mr-2" />
                Send Message
              </Button>
            </form>
          )}
    </div>
  );
};

export default ContactForm;
